import { useTranslation } from 'react-i18next';

import {
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

type InvalidateArg<TData, TVariables> =
  | QueryKey[]
  | ((data: TData, variables: TVariables) => QueryKey[])
  | false;

export interface FeatureMutationOptions<TData, TVariables>
  extends Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidate?: InvalidateArg<TData, TVariables>;
  successMessage?: string;
}

export type UseFeatureMutation = <TData, TVariables = void>(
  options: FeatureMutationOptions<TData, TVariables>,
) => UseMutationResult<TData, unknown, TVariables>;

export function createUseFeatureMutation(defaultInvalidateKeys: QueryKey[]): UseFeatureMutation {
  return function useFeatureMutation<TData, TVariables = void>(
    options: FeatureMutationOptions<TData, TVariables>,
  ): UseMutationResult<TData, unknown, TVariables> {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { mutationFn, invalidate, successMessage, onSuccess, ...rest } = options;

    return useMutation<TData, unknown, TVariables>({
      mutationFn,
      onSuccess: async (data, variables, context) => {
        const keysToInvalidate = resolveInvalidateKeys(
          invalidate,
          defaultInvalidateKeys,
          data,
          variables,
        );
        await Promise.all(
          keysToInvalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        );
        if (successMessage) {
          enqueueSnackbar(t(successMessage), { variant: 'success' });
        }
        await onSuccess?.(data, variables, context);
      },
      ...rest,
    });
  };
}

function resolveInvalidateKeys<TData, TVariables>(
  invalidate: InvalidateArg<TData, TVariables> | undefined,
  defaults: QueryKey[],
  data: TData,
  variables: TVariables,
): QueryKey[] {
  if (invalidate === false) return [];
  if (invalidate === undefined) return defaults;
  if (typeof invalidate === 'function') return invalidate(data, variables);
  return invalidate;
}
